import { useParams, useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { LogOut, Camera, User, BarChart3 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ImageSolverModal } from '@/components/ImageSolverModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SemesterPage = () => {
  const { gradeId } = useParams();
  const navigate = useNavigate();
  const [gradeName, setGradeName] = useState('');
  const [solverOpen, setSolverOpen] = useState(false);
  const { user, logout, getAuthHeaders } = useAuth();

  useEffect(() => {
    const fetchGrade = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/grades`, { headers: getAuthHeaders() });
        const grade = res.data.find(g => g.id === gradeId);
        if (grade) setGradeName(grade.name_ar);
      } catch (error) {
        console.error(error);
      }
    };
    fetchGrade();
  }, [gradeId, getAuthHeaders]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const semesters = [
    { id: 1, title: 'الفصل الدراسي الأول', cls: '' },
    { id: 2, title: 'الفصل الدراسي الثاني', cls: 'two' },
  ];

  const parts = (gradeName || '').trim().split(' ');
  const h1first = parts.length > 1 ? parts.slice(0, -1).join(' ') : (gradeName || 'الفصول الدراسية');
  const h1last = parts.length > 1 ? parts[parts.length - 1] : '';

  return (
    <div className="smpage" dir="rtl" data-testid="semester-page">
      {/* Header */}
      <header className="nav" data-testid="main-header">
        <button className="brand" onClick={() => navigate('/grades')} aria-label="الرجوع" data-testid="back-btn">
          <span>RasheedGPT</span>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="who" data-testid="user-menu-trigger">
              <i aria-hidden="true">{user?.name?.charAt(0)}</i>
              <span className="hidden sm:inline">{user?.name}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48 bg-white border-black/10 text-[#1f1a33]">
            <div className="px-3 py-2 border-b border-black/5">
              <p className="font-semibold text-[#1f1a33]">{user?.name}</p>
              <p className="text-xs text-[#625c78]">{user?.role === 'admin' ? 'مدير' : user?.role === 'student' ? 'طالب' : 'ولي أمر'}</p>
            </div>
            {user?.role === 'admin' && (
              <DropdownMenuItem asChild className="text-[#4a3aa8] hover:text-[#4a3aa8] hover:bg-black/5 cursor-pointer">
                <Link to="/admin"><BarChart3 className="w-4 h-4 me-2" />لوحة التحكم</Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild className="text-[#1f1a33]/80 hover:text-[#1f1a33] hover:bg-black/5 cursor-pointer">
              <Link to="/grades">الصفوف</Link>
            </DropdownMenuItem>
            {user?.role !== 'admin' && (
              <DropdownMenuItem asChild className="text-[#1f1a33]/80 hover:text-[#1f1a33] hover:bg-black/5 cursor-pointer">
                <Link to="/profile" data-testid="profile-link"><User className="w-4 h-4 me-2" />الملف الشخصي</Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => setSolverOpen(true)} className="text-[#1f1a33]/80 hover:text-[#1f1a33] hover:bg-black/5 cursor-pointer" data-testid="solve-question-btn">
              <Camera className="w-4 h-4 me-2" />حل سؤال
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} className="text-red-500 hover:text-red-600 hover:bg-black/5 cursor-pointer" data-testid="logout-button">
              <LogOut className="w-4 h-4 me-2" />تسجيل الخروج
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div className="rings" aria-hidden="true"><i></i><i></i><i></i></div>

      <main>
        <div className="hd">
          <span className="badge">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 1l11 11-11 11L1 12z" /></svg>
            اختر الفصل الدراسي
          </span>
          <h1 data-testid="semester-title">{h1first} {h1last && <span>{h1last}</span>}</h1>
          <p>اختر الفصل الدراسي لتبدأ رحلتك</p>
        </div>

        <nav className="gr" aria-label="الفصول الدراسية">
          {semesters.map((sem, i) => (
            <button
              key={sem.id}
              className={`sm ${sem.cls}`}
              style={{ '--i': i }}
              onClick={() => navigate(`/grades/${gradeId}/semester/${sem.id}/subjects`)}
              data-testid={`semester-${sem.id}-btn`}
            >
              <div className="no">{sem.id}</div>
              <h2>{sem.title}</h2>
              <span className="go">ابدأ<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg></span>
            </button>
          ))}
        </nav>
      </main>

      <ImageSolverModal open={solverOpen} onOpenChange={setSolverOpen} />
    </div>
  );
};

export default SemesterPage;
